/*!
 * Copyright © 2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import { Component, ChangeDetectionStrategy, Input, OnChanges } from '@angular/core';
import { EAssetBatteryState, EVehicleStatusType, IUserSettings } from '../../../core/models';
import { PlotFormattingService } from '../../../core/services';

@Component({
  selector: 'fm-ev-battery-section',
  templateUrl: './ev-battery-section.component.html',
  styleUrls: ['./ev-battery-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EvBatterySectionComponent implements OnChanges {
  // Required
  @Input()
  isCharging: boolean;
  @Input()
  percentage: number;
  @Input()
  userSettings: IUserSettings;

  // Optional for stale state
  @Input()
  vehicleStatus: EVehicleStatusType;
  @Input()
  batteryUpdateTime: Date;

  // Optional for tooltip
  @Input()
  isTooltip: boolean;

  @Input()
  showBatteryPercentageOnRightSide = false;

  truncatedBatteryPercentage: number;
  batteryState: EAssetBatteryState;
  EAssetBatteryState = EAssetBatteryState;
  isBatteryInfoStale: boolean;
  isVehicleMovingOrIdle: boolean;
  totalTimeElapsedSinceLastBatteryUpdateMinutes: number;
  formattedLastBatteryUpdate: string;
  isPrivacyModeEnabled: boolean;
  shouldDisplayTooltip: boolean;

  private readonly LOW_BATTERY_PERCENTAGE = 20;
  private readonly EMPTY_BATTERY_PERCENTAGE = 1;

  constructor(private readonly _plotFormattingService: PlotFormattingService) {}

  ngOnChanges(): void {
    this._setVehicleStatus();
    this._setBatteryState();
    this._truncateBatteryPercentage();
    if (this.batteryUpdateTime && this.vehicleStatus) {
      this._checkIfBatteryInformationIsStale();
      this._formatStaleBatteryDuration();
    }
    this.shouldDisplayTooltip = (this.isBatteryInfoStale && !this.showBatteryPercentageOnRightSide) || this.isPrivacyModeEnabled;
  }
  private _setVehicleStatus(): void {
    if (this.vehicleStatus === EVehicleStatusType.Private) {
      this.isPrivacyModeEnabled = true;
    } else {
      this.isPrivacyModeEnabled = false;
    }

    if (this.vehicleStatus === EVehicleStatusType.Moving || this.vehicleStatus === EVehicleStatusType.Idle) {
      this.isVehicleMovingOrIdle = true;
    } else {
      this.isVehicleMovingOrIdle = false;
    }
  }

  private _setBatteryState(): void {
    if (!this.isCharging) {
      if (this.percentage > this.LOW_BATTERY_PERCENTAGE) {
        this.batteryState = EAssetBatteryState.Normal;
      } else if (this.percentage >= this.EMPTY_BATTERY_PERCENTAGE) {
        this.batteryState = EAssetBatteryState.Low;
      } else {
        this.batteryState = EAssetBatteryState.Empty;
      }
    }
  }

  private _truncateBatteryPercentage(): void {
    this.truncatedBatteryPercentage = this.batteryState === EAssetBatteryState.Empty ? 0 : Math.trunc(this.percentage);
  }

  private _checkIfBatteryInformationIsStale(): void {
    if (!this.isVehicleMovingOrIdle && !this.isPrivacyModeEnabled && !this.isCharging) {
      this.isBatteryInfoStale = false;
      return;
    }
    this.totalTimeElapsedSinceLastBatteryUpdateMinutes = Math.floor((Date.now() - this.batteryUpdateTime.getTime()) / 60000);
    this.isBatteryInfoStale = this._isBatteryInfoStale();
  }

  private _formatStaleBatteryDuration(): void {
    if (!this.isBatteryInfoStale) {
      return;
    }
    this.formattedLastBatteryUpdate = this._plotFormattingService.formatLastUpdateForBatteryDisplay(
      this.batteryUpdateTime,
      this.userSettings
    );
  }

  private _isBatteryInfoStale(): boolean {
    if (this.isPrivacyModeEnabled) {
      return true;
    }

    if (this.isCharging) {
      return this.totalTimeElapsedSinceLastBatteryUpdateMinutes > 180;
    }

    return this.totalTimeElapsedSinceLastBatteryUpdateMinutes > 15;
  }
}
